import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface CompanyOption {
  tenantId: number;
  name: string;
}

// Every company in the system, for SUPERADMIN's switcher and the per-row Company
// column on every in-scope data page. Backed by the same /admin/admins endpoint
// CompanyManagement.tsx already uses — for a SUPERADMIN caller with no impersonation
// header it already returns every ADMIN/SUPERADMIN row unscoped, so no backend change
// was needed for this hook. Only SUPERADMIN ever needs this list, and the request must
// skip the impersonation header (see api.ts) or a single-company scope would collapse
// the switcher's own dropdown down to just that one company.
export const useAllCompanies = () => {
  const { user } = useAuth();
  const u = user as any;
  return useQuery<any, Error, CompanyOption[]>({
    queryKey: ['all-companies'],
    // SUPERADMIN gets every company; a reseller gets the companies they created
    // (/admin/admins is scoped by parentResellerId server-side). x-skip-impersonation
    // keeps this request unscoped so the list never collapses to one company.
    queryFn: () => apiService.get('/admin/admins', { headers: { 'x-skip-impersonation': 'true' } }),
    enabled: user?.role === 'SUPERADMIN' || !!u?.isReseller,
    select: (data: any): CompanyOption[] => {
      const admins = data?.admins || [];
      const list: CompanyOption[] = admins
        .filter((a: any) => a.role !== 'SUPERADMIN' && a.tenantId != null && a.tenantId === a.id)
        .map((a: any) => ({ tenantId: a.tenantId as number, name: a.companyName || a.name }));
      // A reseller's own workspace isn't in /admin/admins (it has no parentResellerId),
      // but the merged "all my companies" view includes its rows — add it so they
      // resolve to a name instead of "#<id>".
      if (u?.isReseller && u?.tenantId != null && !list.some((c) => c.tenantId === u.tenantId)) {
        list.push({ tenantId: u.tenantId as number, name: u.companyName || 'My workspace' });
      }
      return list.sort((x: CompanyOption, y: CompanyOption) => x.name.localeCompare(y.name));
    },
  });
};
